import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { Calendar as CalendarIcon } from "lucide-react";

import { Button } from "../../../../components/ui/button";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Switch } from "../../../../components/ui/switch";
import { Calendar } from "../../../../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../../../../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from "../../../../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import { UploadBox } from "./UploadBox";
import { ViewContext } from "components/lib";
import { stateOptions } from "data";
import { useEffect } from "react";
import { z } from "zod";
import { parse, format } from "date-fns";

const noSpecialCharactersRegex = /^[A-Za-z0-9 ]*$/;

export const driverCreateSchema = z
  .object({
    firstName: z
      .string()
      .min(1, "First Name is required")
      .regex(
        noSpecialCharactersRegex,
        "First Name must not contain special characters"
      ),
    lastName: z
      .string()
      .min(1, "Last Name is required")
      .regex(
        noSpecialCharactersRegex,
        "Last Name must not contain special characters"
      ),

    dob: z.coerce
      .date({
        required_error: "Date of Birth is required",
        invalid_type_error: "Invalid date",
      })
      .refine(
        (date) => {
          const today = new Date();
          const age = today.getFullYear() - date.getFullYear();
          const monthDiff = today.getMonth() - date.getMonth();
          const dayDiff = today.getDate() - date.getDate();

          const actualAge =
            monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

          return actualAge >= 23;
        },
        { message: "Driver must be at least 23 years old" }
      ),

    dateOfHire: z.coerce
      .date({
        invalid_type_error: "Invalid date",
      }).optional().nullable(),

    yearsExp: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({ invalid_type_error: "Experience must be a number" })
        .min(2, "Minimum 2 years of commercial driving experience required")
    ),

    licenseState: z.string().min(1, "License State is required"),
    licenseNumber: z.string().min(1, "License Number is required"),

    exclude: z.boolean().default(false),

    mvrAccidents: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0, "Must be at least 0")
    ),
    mvrViolations: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0, "Must be at least 0")
    ),
    mvrSuspensions: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0, "Must be at least 0")
    ),

    status: z.enum(["active", "inactive"]).optional(),

    frontImg: z.preprocess(
      (val) => (typeof val === "string" ? null : val),
      z.instanceof(File).optional().nullable()
    ),
    backImg: z.preprocess(
      (val) => (typeof val === "string" ? null : val),
      z.instanceof(File).optional().nullable()
    ),
  })
  .superRefine((data, ctx) => {
    const today = new Date();
    const age = today.getFullYear() - data.dob.getFullYear();
    const monthDiff = today.getMonth() - data.dob.getMonth();
    const dayDiff = today.getDate() - data.dob.getDate();
    const actualAge =
      monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

    const maxExperience = actualAge - 18;

    if (typeof data.yearsExp === "number" && data.yearsExp > maxExperience) {
      ctx.addIssue({
        path: ["yearsExp"],
        code: "custom",
        message: `Experience cannot exceed ${maxExperience} years (age - 18)`,
      });
    }
  });

export function UpsertDriverDialog({
  open,
  onOpenChange,
  mode, // "create" | "edit"
  initial, // optional: in-form driver shape (for edit)
  companyId,
  onDone, // (driverAddedOrUpdated) => void
}) {
  const context = React.useContext(ViewContext);

  const defaultValues = React.useMemo(() => {
    if (mode === "edit" && initial) {
      // hydrate for form editing
      const dob = initial.dateOfBirth ? initial.dateOfBirth : null;
      const doh = initial.dateOfHire
        ? initial.dateOfHire
        : null;

      return {
        firstName: initial.firstName,
        lastName: initial.lastName,
        dob,
        dateOfHire: doh,
        yearsExp: initial.yearsExperience,
        licenseState: initial.licenseState,
        licenseNumber: initial.licenseNumber,
        exclude: !!initial.exclude,
        mvrAccidents: initial.accidents,
        mvrViolations: initial.violations,
        mvrSuspensions: initial.suspensions,
        status: initial.status ?? "active",
        frontImg: initial.frontImg,
        backImg: initial.backImg,
      };
    }
    return {
      firstName: "",
      lastName: "",
      dob: undefined,
      dateOfHire: undefined,
      yearsExp: "",
      licenseState: stateOptions[0]?.value ?? "Alabama",
      licenseNumber: "",
      exclude: false,
      mvrAccidents: "",
      mvrViolations: "",
      mvrSuspensions: "",
      status: "active",
      frontImg: null,
      backImg: null,
    };
  }, [mode, initial]);

  const form = useForm({
    resolver: zodResolver(driverCreateSchema),
    defaultValues,
    mode: "all",
    reValidateMode: "onBlur",
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { isSubmitting, errors, isDirty },
  } = form;

  // Local calendar popovers:
  const [dobOpen, setDobOpen] = React.useState(false);
  const [dohOpen, setDohOpen] = React.useState(false);

  const toISO = (d) => (d ? new Date(d).toISOString().substring(0, 10) : null);

  const postFilesIfAny = async (driverId, frontImg, backImg) => {
    if (!frontImg && !backImg) return;
    const formData = new FormData();
    if (frontImg) formData.append("front_license", frontImg);
    if (backImg) formData.append("back_license", backImg);
    if (frontImg || backImg) {
      await axios.post(`/api/driver/${driverId}/file`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      const payload = {
        name: `${values.firstName} ${values.lastName}`.trim(),
        license_number: values.licenseNumber.replaceAll("-", ""),
        license_state: values.licenseState,
        date_of_birth: toISO(values.dob),
        date_of_hire: toISO(values.dateOfHire),
        accidents: String(values.mvrAccidents),
        violations: String(values.mvrViolations),
        suspensions: String(values.mvrSuspensions),
        exclude_from_policy: !!values.exclude,
        years_of_experience: String(values.yearsExp),
        company_id: companyId,
        ...(mode === "edit" ? { status: values.status } : {}),
      };

      if (mode === "create") {
        const res = await axios.post(`/api/driver`, payload);
        const newId = res?.data?.data?.id;

        // Upload images if present
        await postFilesIfAny(
          newId,
          values.frontImg === initial?.frontImg ? null : values.frontImg,
          values.backImg === initial?.backImg ? null : values.backImg
        );

        context.notification?.show?.(
          "Driver created successfully!",
          "success",
          true,
        );

        // Return normalized in-form driver object:
        const ret = {
          id: newId,
          driverId: newId,
          firstName: values.firstName,
          lastName: values.lastName,
          dateOfBirth: payload.date_of_birth,
          dateOfHire: payload.date_of_hire,
          licenseNumber: payload.license_number,
          licenseState: payload.license_state,
          yearsExperience: Number(values.yearsExp),
          accidents: Number(values.mvrAccidents),
          violations: Number(values.mvrViolations),
          suspensions: Number(values.mvrSuspensions),
          exclude: !!values.exclude,
          eligibility: "Covered",
          status: "active",
          backImg: values.backImg,
          frontImg: values.frontImg,
        };
        onDone?.(ret);
      } else {
        const id = initial?.driverId;
        if (!id) {
          context.notification?.show?.(
            "Missing driver id for edit.",
            "error",
            true
          );
          return;
        }

        await axios.patch(`/api/driver/${id}`, payload);
        await postFilesIfAny(
          id,
          values.frontImg === initial?.frontImg ? null : values.frontImg,
          values.backImg === initial?.backImg ? null : values.backImg
        );

        context.notification?.show?.(
          "Driver updated successfully!",
          "success",
          true,
          "toast",
          "check-circle"
        );

        const ret = {
          ...(initial ?? {}),
          firstName: values.firstName,
          lastName: values.lastName,
          dateOfBirth: payload.date_of_birth,
          dateOfHire: payload.date_of_hire,
          licenseNumber: payload.license_number,
          licenseState: payload.license_state,
          yearsExperience: Number(values.yearsExp),
          accidents: Number(values.mvrAccidents),
          violations: Number(values.mvrViolations),
          suspensions: Number(values.mvrSuspensions),
          exclude: !!values.exclude,
          status: values.status ?? "active",
          backImg: values.backImg,
          frontImg: values.frontImg,
        };
        onDone?.(ret);
      }

      onOpenChange(false);
    } catch (err) {
      console.error("Error creating driver", err);

      const msg =
        err?.response?.data?.message ||
        (mode === "create"
          ? "Failed to create driver."
          : "Failed to update driver.");

      if (msg === 'Any driver found with that id!') {
        context.notification?.show?.(
          "Driver not found.",
          "error",
          true
        );
        onDone?.({}, true);
        onOpenChange(false);
        return;
      }
      // Show error on top of the form (general form-level error)
      setError("root", {
        type: "server",
        message: msg,
      });
    }
  });

  // License number: strip dashes on type
  const licenseNumber = watch("licenseNumber");
  const dob = watch("dob");
  const doh = watch("dateOfHire");

  React.useEffect(() => {
    if (!licenseNumber) return;
    const sanitized = licenseNumber.replaceAll("-", "");
    if (sanitized !== licenseNumber)
      setValue("licenseNumber", sanitized, { shouldDirty: true });
  }, [licenseNumber, setValue]);

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues); // instead of looping through setValue
    }
  }, [defaultValues, form]);

  const today = new Date();
  const cutoff = new Date(today);
  cutoff.setFullYear(cutoff.getFullYear() - 23);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add Driver" : "Edit Driver"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Row 1 */}
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-1 space-y-2">
              <Label>First Name *</Label>
              <Controller
                control={control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <>
                    <Input {...field} placeholder="First Name" />
                    {fieldState.error && (
                      <p className="text-xs text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
            <div className="md:col-span-1 space-y-2">
              <Label>Last Name *</Label>
              <Controller
                control={control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <>
                    <Input {...field} placeholder="Last Name" />
                    {fieldState.error && (
                      <p className="text-xs text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
            <div className="md:col-span-1 space-y-2">
              <Label>Date of Birth *</Label>
              <Controller
                control={control}
                name="dob"
                render={({ field }) => (
                  <Popover
                    open={dobOpen}
                    onOpenChange={(v) => {
                      field.onChange(field.value);
                      setDobOpen(v);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between font-normal"
                      >
                        {dob
                          ? dob instanceof Date
                            ? dob?.toLocaleDateString() : dob
                          : "Select date"}
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto overflow-hidden p-0"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={(d) => {
                          field.onChange(d ?? null);
                          setDobOpen(false);
                        }}
                        captionLayout="dropdown"
                        className="min-w-60"
                        disabled={(date) => {
                          return date > today || date > cutoff;
                        }}
                        defaultMonth={field.value || new Date(cutoff.getFullYear(), cutoff.getMonth())}
                        endMonth={new Date(cutoff.getFullYear(), cutoff.getMonth() + 1)}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
              {form.formState.errors?.dob && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.dob.message}
                </p>
              )}
            </div>
            <div className="md:col-span-1 space-y-2">
              <Label>Date of Hire</Label>
              <Controller
                control={control}
                name="dateOfHire"
                render={({ field }) => (
                  <Popover
                    open={dohOpen}
                    onOpenChange={(v) => {
                      field.onChange(field.value);
                      setDohOpen(v);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between font-normal"
                      >
                        {doh
                          ? doh instanceof Date ? doh?.toLocaleDateString() : doh
                          : "Select date"}
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto overflow-hidden p-0"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={(d) => {
                          field.onChange(d);
                          setDohOpen(false);
                        }}
                        captionLayout="dropdown"
                        className="min-w-60"
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
              {form.formState.errors?.dateOfHire && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.dateOfHire.message}
                </p>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Years of Experience *</Label>
              <Controller
                control={control}
                name="yearsExp"
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      placeholder="Years of Experience"
                      onChange={(e) => {
                        field.onChange(
                          e.target.value === ""
                            ? undefined
                            : Number(e.target.value)
                        );
                      }}
                      onBlur={() => {
                        field.onChange(field.value);
                      }}
                    />
                    {fieldState.error && (
                      <p className="text-xs text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>License State *</Label>
              <Controller
                control={control}
                name="licenseState"
                render={({ field, fieldState }) => (
                  <>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full !h-10 py-2 px-3">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        <SelectGroup>
                          <SelectLabel>States</SelectLabel>
                          {stateOptions.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {fieldState.error && (
                      <p className="text-xs text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>License Number *</Label>
              <Controller
                control={control}
                name="licenseNumber"
                render={({ field, fieldState }) => (
                  <>
                    <Input {...field} placeholder="License Number" />
                    {fieldState.error && (
                      <p className="text-xs text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          {/* Uploads */}
          <div className="grid gap-4 md:grid-cols-2">
            <Controller
              control={control}
              name="frontImg"
              render={({ field }) => (
                <UploadBox
                  id="front-license"
                  label="Front License Picture"
                  onFile={(f) => field.onChange(f)}
                  required={false}
                  defaultImg={initial?.frontImg}
                />
              )}
            />
            <Controller
              control={control}
              name="backImg"
              render={({ field }) => (
                <UploadBox
                  id="back-license"
                  label="Back License Picture"
                  onFile={(f) => field.onChange(f)}
                  required={false}
                  defaultImg={initial?.backImg}
                />
              )}
            />
          </div>

          {/* Exclude + (Status for edit) */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 items-center gap-3 flex">
              <Controller
                control={control}
                name="exclude"
                render={({ field }) => (
                  <Switch
                    id="exclude-driver"
                    checked={!!field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label htmlFor="exclude-driver" className="cursor-pointer">
                Exclude this driver from the policy?
              </Label>
            </div>

            {mode === "edit" && (
              <div className="flex flex-1 items-center gap-3">
                <Label className="min-w-[60px]">Status</Label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-40 !h-10 py-2 px-3">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}
          </div>

          {/* MVR */}
          <div className="space-y-2">
            <div className="text-sm font-medium">
              MVR Summary (prior 36 months)
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {["mvrAccidents", "mvrViolations", "mvrSuspensions"].map(
                (name, idx) => (
                  <div className="space-y-2" key={name}>
                    <Label>
                      {idx === 0
                        ? "Accidents *"
                        : idx === 1
                        ? "Violations *"
                        : "Suspensions *"}
                    </Label>
                    <Controller
                      control={control}
                      name={name}
                      render={({ field, fieldState }) => (
                        <>
                          <Input
                            {...field}
                            type="number"
                            min={0}
                            placeholder={
                              idx === 0
                                ? "Accidents"
                                : idx === 1
                                ? "Violations"
                                : "Suspensions"
                            }
                          />
                          {fieldState.error && (
                            <p className="text-xs text-red-600">
                              {fieldState.error.message}
                            </p>
                          )}
                        </>
                      )}
                    />
                  </div>
                )
              )}
            </div>
            {errors.root && (
              <div className="text-sm text-red-600 mb-2">
                {errors.root.message}
              </div>
            )}

          </div>

          <DialogFooter className="pt-2">
            <DialogClose asChild>
              <Button variant="outline" disabled={isSubmitting}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isSubmitting || (mode === "edit" && !isDirty)}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
